import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDistanceToNow } from 'date-fns';
import { EyeOff, History, RotateCcw, Trash2 } from 'lucide-react';
import type { TrackerSourcePostingReference } from '@shared/tracker-types';
import {
  getOriginalImageAltLabel,
  getReferenceHistoryStepCount,
  getReferenceMetaLabel,
  getReferenceOperatorVerb,
  getReferencePanelCopyForKind,
  getReferenceTimestampLabel,
  formatReferenceFormatSummaries,
  groupReferencesByKind,
  type ReferenceKind,
} from '@/components/proof-of-posting/submissionReferenceLabels';

export type RemovalSourcePostingReference = TrackerSourcePostingReference;

interface Props {
  references: RemovalSourcePostingReference[];
  variant?: 'card' | 'sidebar' | 'lightbox';
}

function panelStyle(referenceKind: ReferenceKind) {
  switch (referenceKind) {
    case 'repost_rejected_removal':
      return {
        icon: Trash2,
        surfaceClass: 'border-orange-200 bg-orange-50/50',
        badgeClass: 'border-orange-300 bg-orange-100 text-orange-900',
        primaryBadgeClass: 'bg-orange-700 hover:bg-orange-700 text-white',
        accentTextClass: 'text-orange-900',
      };
    case 'repost_rejected':
      return {
        icon: RotateCcw,
        surfaceClass: 'border-amber-200 bg-amber-50/50',
        badgeClass: 'border-amber-300 bg-amber-100 text-amber-900',
        primaryBadgeClass: 'bg-amber-700 hover:bg-amber-700 text-white',
        accentTextClass: 'text-amber-900',
      };
    case 'removal_source':
    default:
      return {
        icon: History,
        surfaceClass: 'border-violet-200 bg-violet-50/50',
        badgeClass: 'border-violet-300 bg-violet-100 text-violet-900',
        primaryBadgeClass: 'bg-violet-700 hover:bg-violet-700 text-white',
        accentTextClass: 'text-violet-900',
      };
  }
}

function OriginalReferenceImage({
  reference,
  imageLabel,
  className,
  showOverlay = true,
}: {
  reference: RemovalSourcePostingReference;
  imageLabel: string;
  className: string;
  showOverlay?: boolean;
}) {
  const alt = getOriginalImageAltLabel(reference.referenceKind, reference.formatName);
  const overlayLabel = reference.formatSummaries && reference.formatSummaries.length > 1
    ? imageLabel
    : reference.formatSummaries?.length
      ? formatReferenceFormatSummaries(reference.formatSummaries, reference)
      : (reference.formatName ?? imageLabel);

  return (
    <a
      href={reference.imageUrl ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block"
    >
      <img
        src={reference.imageUrl ?? undefined}
        alt={alt}
        className={className}
      />
      {showOverlay ? (
        <span className="absolute left-2 top-2 rounded-md bg-black/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          {overlayLabel}
        </span>
      ) : null}
    </a>
  );
}

function ReferenceMeta({
  reference,
  compact = false,
}: {
  reference: RemovalSourcePostingReference;
  compact?: boolean;
}) {
  const timestampLabel = getReferenceTimestampLabel(reference.referenceKind);
  const operatorVerb = getReferenceOperatorVerb(reference.referenceKind);

  return (
    <>
      <div className="font-medium text-foreground">
        {formatReferenceFormatSummaries(reference.formatSummaries, reference)}
      </div>
      <div className="text-muted-foreground">
        {getReferenceMetaLabel(reference, { compact })}
        {reference.postingEventId ? ` · event #${reference.postingEventId}` : ''}
      </div>
      {(reference.postingOperatorName || reference.approvedAt) && (
        <div className="text-muted-foreground">
          {reference.postingOperatorName ? `${operatorVerb} by ${reference.postingOperatorName}` : null}
          {reference.postingOperatorName && reference.approvedAt ? ' · ' : null}
          {reference.approvedAt
            ? `${timestampLabel} ${formatDistanceToNow(new Date(reference.approvedAt), { addSuffix: true })}`
            : null}
        </div>
      )}
    </>
  );
}

function ReferenceHeader({
  referenceKind,
  variant,
  step,
  totalSteps,
}: {
  referenceKind: ReferenceKind;
  variant: Props['variant'];
  step: number;
  totalSteps: number;
}) {
  const copy = getReferencePanelCopyForKind(referenceKind, step, totalSteps);
  const style = panelStyle(referenceKind);
  const Icon = style.icon;
  const compact = variant === 'lightbox' || variant === 'sidebar';

  if (variant === 'lightbox') {
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`gap-1 ${style.primaryBadgeClass}`}>
            <Icon className="h-3 w-3" />
            {copy.badgeLabel}
          </Badge>
          <Badge variant="outline" className={`gap-1 ${style.badgeClass}`}>
            <EyeOff className="h-3 w-3" />
            {copy.notUnderReviewLabel}
          </Badge>
        </div>
        <p className="text-xs font-semibold text-foreground">{copy.title}</p>
        <p className="text-[11px] leading-snug text-muted-foreground">{copy.description}</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={`gap-1 ${style.primaryBadgeClass}`}>
          <Icon className="h-3 w-3" />
          {copy.badgeLabel}
        </Badge>
        <span className={`${compact ? 'text-xs font-semibold' : 'text-sm font-semibold'} text-foreground`}>
          {copy.title}
        </span>
        <Badge variant="outline" className={`gap-1 ${style.badgeClass}`}>
          <EyeOff className="h-3 w-3" />
          {copy.notUnderReviewLabel}
        </Badge>
      </div>
      <p className={`${compact ? 'text-[11px] font-medium' : 'text-xs font-medium'} ${style.accentTextClass}`}>
        {copy.subtitle}
      </p>
      <p className={`${compact ? 'text-[11px]' : 'text-xs'} leading-snug text-muted-foreground`}>
        {copy.description}
      </p>
    </div>
  );
}

function ReferenceImageList({
  references,
  referenceKind,
  variant,
}: {
  references: RemovalSourcePostingReference[];
  referenceKind: ReferenceKind;
  variant: Props['variant'];
}) {
  const copy = getReferencePanelCopyForKind(referenceKind);
  const compactMeta = variant === 'lightbox';

  if (variant === 'sidebar') {
    return (
      <div className="space-y-2">
        {references.map((reference) => (
          <div
            key={`${referenceKind}-${reference.postingEventId ?? 'x'}-${reference.formatId}`}
            className="flex gap-2 rounded-md border bg-white/90 p-2"
          >
            <OriginalReferenceImage
              reference={reference}
              imageLabel={copy.imageLabel}
              className="h-16 w-16 flex-shrink-0 rounded object-cover"
              showOverlay={false}
            />
            <div className="min-w-0 space-y-0.5 text-xs">
              <ReferenceMeta reference={reference} compact={compactMeta} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const imageClassName = variant === 'lightbox'
    ? 'max-h-[320px] w-full object-cover transition-transform group-hover:scale-[1.01]'
    : 'h-40 w-full object-cover transition-transform group-hover:scale-[1.01]';

  return (
    <div className={references.length > 1 && variant === 'card' ? 'grid gap-3 sm:grid-cols-2' : 'space-y-3'}>
      {references.map((reference) => (
        <div
          key={`${referenceKind}-${reference.postingEventId ?? 'x'}-${reference.formatId}`}
          className="overflow-hidden rounded-lg border bg-white/90"
        >
          <OriginalReferenceImage
            reference={reference}
            imageLabel={copy.imageLabel}
            className={imageClassName}
            showOverlay={variant !== 'lightbox'}
          />
          <div className="space-y-1 px-3 py-2 text-xs">
            <ReferenceMeta reference={reference} compact={compactMeta} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ReferenceKindPanel({
  references,
  referenceKind,
  variant,
  step,
  totalSteps,
}: {
  references: RemovalSourcePostingReference[];
  referenceKind: ReferenceKind;
  variant: Props['variant'];
  step: number;
  totalSteps: number;
}) {
  const style = panelStyle(referenceKind);

  if (variant === 'lightbox') {
    return (
      <div className={`flex w-[min(320px,28vw)] flex-shrink-0 flex-col gap-3 self-start rounded-xl border p-3 shadow-sm ${style.surfaceClass}`}>
        <ReferenceHeader referenceKind={referenceKind} variant={variant} step={step} totalSteps={totalSteps} />
        <ReferenceImageList references={references} referenceKind={referenceKind} variant={variant} />
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className={`rounded-lg border p-3 ${style.surfaceClass}`}>
        <ReferenceHeader referenceKind={referenceKind} variant={variant} step={step} totalSteps={totalSteps} />
        <div className="mt-2">
          <ReferenceImageList references={references} referenceKind={referenceKind} variant={variant} />
        </div>
      </div>
    );
  }

  return (
    <Card className={style.surfaceClass}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          <ReferenceHeader referenceKind={referenceKind} variant={variant} step={step} totalSteps={totalSteps} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ReferenceImageList references={references} referenceKind={referenceKind} variant={variant} />
      </CardContent>
    </Card>
  );
}

export function RemovalSourcePostingReferences({ references, variant = 'card' }: Props) {
  const groups = groupReferencesByKind(references);
  if (groups.length === 0) {
    return null;
  }

  const totalSteps = getReferenceHistoryStepCount(references);

  if (variant === 'lightbox') {
    return (
      <>
        {groups.map(({ kind, references: groupRefs }, index) => (
          <ReferenceKindPanel
            key={kind}
            references={groupRefs}
            referenceKind={kind}
            variant={variant}
            step={index + 1}
            totalSteps={totalSteps}
          />
        ))}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map(({ kind, references: groupRefs }, index) => (
        <ReferenceKindPanel
          key={kind}
          references={groupRefs}
          referenceKind={kind}
          variant={variant}
          step={index + 1}
          totalSteps={totalSteps}
        />
      ))}
    </div>
  );
}
